import React from 'react';
import FormField from '../shared/FormField';
import CountrySelect, { getCountryName } from '../shared/CountrySelect';
import ProvinceCitySelect from '../shared/ProvinceCitySelect';
import USStateSelect from '../shared/USStateSelect';
import ZIPCodeInput from '../shared/ZIPCodeInput';
import ChineseZIPCodeInput from '../shared/ChineseZIPCodeInput';
import EmailInput from '../shared/EmailInput';
import PhoneInput from '../shared/PhoneInput';
import formDataStorage from '../../utils/formDataStorage';
import { Plus, Trash2, Home, Phone, Mail, Users, MapPin, Globe } from 'lucide-react';

// 地址电话信息表单
const AddressPhoneForm = ({ data, onChange, fieldErrors = {} }) => {

  // 从formDataStorage读取数据并确保数据同步
  React.useEffect(() => {
    const savedData = formDataStorage.loadFormData('address_phone');
    // 如果有保存的数据但当前data为空或部分字段缺失，则从数据库加载
    if (savedData && (!data || Object.keys(data).length === 0)) {
      Object.keys(savedData).forEach(key => {
        if (savedData[key] !== null && savedData[key] !== undefined) {
          onChange(key, savedData[key]);
        }
      });
    }

    // 确保社交媒体部分至少有一个默认条目
    const currentSocialMedia = savedData.social_media || [];
    if (currentSocialMedia.length === 0) {
      onChange('social_media', [{ social_media: '', social_media_identifier: '' }]);
    }
    // 兜底：当选择“邮寄地址不同”但 mailing_address 缺省某些键时补齐空串
    const isMailingDifferent = (savedData && savedData.mailing_address_same === false);
    const ma = (savedData && savedData.mailing_address) || {};
    const needsPatch = isMailingDifferent && (
      ma.street_address_2 === undefined || ma.street_address === undefined ||
      ma.city === undefined || ma.state === undefined || ma.zip_code === undefined || ma.country === undefined
    );
    if (needsPatch) {
      onChange('mailing_address', {
        street_address: ma.street_address || '',
        street_address_2: ma.street_address_2 || '',
        city: ma.city || '',
        state: ma.state || '',
        zip_code: ma.zip_code || '',
        country: ma.country || ''
      });
    }
  }, [data, onChange]);

  // 社交媒体平台选项（与官方DS-160表单完全一致）
  const SOCIAL_MEDIA_PLATFORMS = [
    { value: 'ASKF', label: 'ASK.FM' },
    { value: 'DUBN', label: 'DOUBAN' },
    { value: 'FCBK', label: 'FACEBOOK' },
    { value: 'FLKR', label: 'FLICKR' },
    { value: 'GOGL', label: 'GOOGLE+' },
    { value: 'INST', label: 'INSTAGRAM' },
    { value: 'LINK', label: 'LINKEDIN' },
    { value: 'MYSP', label: 'MYSPACE' },
    { value: 'PTST', label: 'PINTEREST' },
    { value: 'QZNE', label: 'QZONE (QQ)' },
    { value: 'RDDT', label: 'REDDIT' },
    { value: 'SWBO', label: 'SINA WEIBO' },
    { value: 'TWBO', label: 'TENCENT WEIBO' },
    { value: 'TUMB', label: 'TUMBLR' },
    { value: 'TWIT', label: 'TWITTER' },
    { value: 'TWOO', label: 'TWOO' },
    { value: 'VINE', label: 'VINE' },
    { value: 'VKON', label: 'VKONTAKTE (VK)' },
    { value: 'YUKU', label: 'YOUKU' },
    { value: 'YTUB', label: 'YOUTUBE' },
    { value: 'NONE', label: 'NONE' }
  ];

  // 更新家庭地址
  const updateHomeAddress = (field, value) => {
    const currentHomeAddress = data.home_address || {};
    onChange('home_address', { ...currentHomeAddress, [field]: value });
  };

  // 更新邮寄地址
  const updateMailingAddress = (field, value) => {
    const currentMailingAddress = data.mailing_address || {};
    onChange('mailing_address', { ...currentMailingAddress, [field]: value });
  };

  // 更新其他电话
  const updateOtherPhone = (index, value) => {
    const currentOtherPhones = data.other_phone || [];
    const newOtherPhones = [...currentOtherPhones];
    newOtherPhones[index] = value;
    onChange('other_phone', newOtherPhones.filter(phone => phone));
  };

  // 添加其他电话
  const addOtherPhone = () => {
    const currentOtherPhones = data.other_phone || [];
    const newOtherPhones = [...currentOtherPhones, ''];
    onChange('other_phone', newOtherPhones);
  };

  // 删除其他电话
  const removeOtherPhone = (index) => {
    const currentOtherPhones = data.other_phone || [];
    const newOtherPhones = currentOtherPhones.filter((_, i) => i !== index);
    onChange('other_phone', newOtherPhones);
  };

  // 更新其他邮箱
  const updateOtherEmail = (index, value) => {
    const currentOtherEmails = data.other_email || [];
    const newOtherEmails = [...currentOtherEmails];
    newOtherEmails[index] = value;
    onChange('other_email', newOtherEmails.filter(email => email));
  };

  // 添加其他邮箱
  const addOtherEmail = () => {
    const currentOtherEmails = data.other_email || [];
    const newOtherEmails = [...currentOtherEmails, ''];
    onChange('other_email', newOtherEmails);
  };

  // 删除其他邮箱
  const removeOtherEmail = (index) => {
    const currentOtherEmails = data.other_email || [];
    const newOtherEmails = currentOtherEmails.filter((_, i) => i !== index);
    onChange('other_email', newOtherEmails);
  };

  // 更新社交媒体信息
  const updateSocialMedia = (index, field, value) => {
    const currentSocialMedia = data.social_media || [];
    const newSocialMedia = [...currentSocialMedia];
    if (!newSocialMedia[index]) {
      newSocialMedia[index] = { social_media: '', social_media_identifier: '' };
    }
    newSocialMedia[index] = { ...newSocialMedia[index], [field]: value };
    onChange('social_media', newSocialMedia);
  };

  // 添加社交媒体
  const addSocialMedia = () => {
    const currentSocialMedia = data.social_media || [];
    const newSocialMedia = [...currentSocialMedia, { social_media: '', social_media_identifier: '' }];
    onChange('social_media', newSocialMedia);
  };

  // 删除社交媒体
  const removeSocialMedia = (index) => {
    const currentSocialMedia = data.social_media || [];
    const newSocialMedia = currentSocialMedia.filter((_, i) => i !== index);
    onChange('social_media', newSocialMedia);
  };

  // 更新共享社交媒体信息
  const updateShareSocialMedia = (index, field, value) => {
    const currentShareSocialMedia = data.share_social_media || [];
    const newShareSocialMedia = [...currentShareSocialMedia];
    if (!newShareSocialMedia[index]) {
      newShareSocialMedia[index] = { social_media: '', social_media_identifier: '' };
    }
    newShareSocialMedia[index] = { ...newShareSocialMedia[index], [field]: value };
    onChange('share_social_media', newShareSocialMedia);
  };

  // 添加共享社交媒体
  const addShareSocialMedia = () => {
    const currentShareSocialMedia = data.share_social_media || [];
    const newShareSocialMedia = [...currentShareSocialMedia, { social_media: '', social_media_identifier: '' }];
    onChange('share_social_media', newShareSocialMedia);
  };

  // 删除共享社交媒体
  const removeShareSocialMedia = (index) => {
    const currentShareSocialMedia = data.share_social_media || [];
    const newShareSocialMedia = currentShareSocialMedia.filter((_, i) => i !== index);
    onChange('share_social_media', newShareSocialMedia);
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-6 rounded-lg">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <span className="w-8 h-8 bg-cyan-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">📧</span>
          地址电话信息
        </h3>

        {/* 家庭地址 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
            <Home className="w-5 h-5 mr-2" />
            家庭地址
          </h4>

          <div className="space-y-4">
            <div id="home_street1" />
            <FormField
              label="街道地址（第一行）"
              value={(data.home_address && data.home_address.street_address) || ''}
              onChange={(e) => {
                // 只允许英文字母、数字、空格和常用标点
                const value = e.target.value.replace(/[^\w\s\-\.,#\/]/g, '');
                updateHomeAddress('street_address', value);
              }}
              placeholder="请输入街道地址（仅限英文）"
              required
            />
            <p className="text-xs text-amber-600 -mt-2">注意：仅支持英文字母、数字和常用符号（如 - . , # /）</p>

            <FormField
              label="街道地址（第二行）"
              value={(data.home_address && data.home_address.street_address_2) || ''}
              onChange={(e) => updateHomeAddress('street_address_2', e.target.value)}
              placeholder="公寓号、单元号等（可选）"
            />

            {/* 按照正确顺序：国家 → 州/省 → 城市 → 邮政编码 */}
            <div id="home_country" />
            <CountrySelect
              label="国家"
              value={(data.home_address && data.home_address.country) || ''}
              onChange={(value) => {
                const currentHomeAddress = data.home_address || {};
                onChange('home_address', {
                  ...currentHomeAddress,
                  country: value,
                  state: '', // 清空州/省选择
                  city: ''   // 清空城市选择
                });
              }}
              required
            />

            {/* 根据国家选择显示不同的州/省和城市输入组件 */}
            {(() => {
              const country = (data.home_address && data.home_address.country) || '';
              const isChineseRegion = ['CHIN', 'MAC', 'HNK', 'TWAN'].includes(country);

              if (isChineseRegion) {
                return (
                  <>
                    <div id="home_state" />
                    <div id="home_city" />
                    <ProvinceCitySelect
                    provinceValue={(data.home_address && data.home_address.state) || ''}
                    cityValue={(data.home_address && data.home_address.city) || ''}
                    onProvinceChange={(value) => {
                      const currentHomeAddress = data.home_address || {};
                      onChange('home_address', {
                        ...currentHomeAddress,
                        state: value,
                        city: '' // 清空城市选择
                      });
                    }}
                    onCityChange={(value) => updateHomeAddress('city', value)}
                  />
                  </>
                );
              } else if (country === 'USA') {
                // 美国使用专用的州选择器
                return (
                  <div className="space-y-4">
                    <div id="home_state" />
                    <USStateSelect
                      label="州"
                      value={(data.home_address && data.home_address.state) || ''}
                      onChange={(value) => updateHomeAddress('state', value)}
                      required
                    />
                    <FormField
                      id="home_city"
                      label="城市"
                      value={(data.home_address && data.home_address.city) || ''}
                      onChange={(e) => updateHomeAddress('city', e.target.value)}
                      placeholder="Los Angeles"
                      required
                    />
                  </div>
                );
              } else if (country) {
                // 其他国家
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      id="home_state_text"
                      label="州/省"
                      value={(data.home_address && data.home_address.state) || ''}
                      onChange={(e) => updateHomeAddress('state', e.target.value)}
                      placeholder="省份或州名"
                      required
                    />
                    <FormField
                      id="home_city_text"
                      label="城市"
                      value={(data.home_address && data.home_address.city) || ''}
                      onChange={(e) => updateHomeAddress('city', e.target.value)}
                      placeholder="城市名"
                      required
                    />
                  </div>
                );
              }
              return null;
            })()}

            {/* 邮政编码放在最后 */}
            {(() => {
              const country = (data.home_address && data.home_address.country) || '';
              const isChineseRegion = ['CHIN', 'MAC', 'HNK', 'TWAN'].includes(country);

              if (isChineseRegion) {
                return (
                  <>
                    <div id="home_zip" />
                    <ChineseZIPCodeInput
                    label="邮政编码"
                    value={(data.home_address && data.home_address.zip_code) || ''}
                    onChange={(value) => updateHomeAddress('zip_code', value)}
                    required
                    countryCode={country}
                  />
                  {fieldErrors['#home_zip'] && (<div className="-mt-2 text-xs text-red-600">{fieldErrors['#home_zip']}</div>)}
                  </>
                );
              } else if (country) {
                return (
                  <>
                    <div id="home_zip" />
                    <ZIPCodeInput
                    label="邮政编码"
                    value={(data.home_address && data.home_address.zip_code) || ''}
                    onChange={(value) => updateHomeAddress('zip_code', value)}
                    required
                  />
                  {fieldErrors['#home_zip'] && (<div className="-mt-2 text-xs text-red-600">{fieldErrors['#home_zip']}</div>)}
                  </>
                );
              }
              return null;
            })()}
          </div>
        </div>

        {/* 邮寄地址 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
            <MapPin className="w-5 h-5 mr-2" />
            邮寄地址
          </h4>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              邮寄地址是否与家庭地址相同？
            </label>
            <div className="flex gap-6">
              <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-cyan-400 hover:bg-cyan-50 transition-all duration-200">
                <input
                  type="radio"
                  name="mailing_address_same"
                  value="true"
                  checked={data.mailing_address_same === true}
                  onChange={(e) => {
                    onChange('mailing_address_same', true);
                    onChange('mailing_address', {});
                  }}
                  className="mr-3 w-5 h-5 text-cyan-600 border-gray-300 focus:ring-cyan-500 focus:ring-2"
                />
                <span className="text-base font-medium text-gray-700">✅ 是 - 与家庭地址相同</span>
              </label>

              <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-cyan-400 hover:bg-cyan-50 transition-all duration-200">
                <input
                  type="radio"
                  name="mailing_address_same"
                  value="false"
                  checked={data.mailing_address_same === false}
                  onChange={(e) => {
                    onChange('mailing_address_same', false);
                    // 初始化 mailing_address 的完整形状，避免缺键
                    const current = data.mailing_address || {};
                    onChange('mailing_address', {
                      street_address: current.street_address || '',
                      street_address_2: current.street_address_2 || '',
                      city: current.city || '',
                      state: current.state || '',
                      zip_code: current.zip_code || '',
                      country: current.country || ''
                    });
                  }}
                  className="mr-3 w-5 h-5 text-cyan-600 border-gray-300 focus:ring-cyan-500 focus:ring-2"
                />
                <span className="text-base font-medium text-gray-700">❌ 否 - 邮寄地址不同</span>
              </label>
            </div>
          </div>

          {data.mailing_address_same === false && (
            <div className="p-4 bg-gradient-to-r from-gray-50 to-cyan-50 rounded-lg border-l-4 border-cyan-400 space-y-4">
              <h5 className="text-md font-semibold text-gray-700 mb-4">邮寄地址详情</h5>

              <div id="mailing_street1" />
              <FormField
                label="街道地址（第一行）"
                value={(data.mailing_address && data.mailing_address.street_address) || ''}
                onChange={(e) => {
                  // 只允许英文字母、数字、空格和常用标点
                  const value = e.target.value.replace(/[^\w\s\-\.,#\/]/g, '');
                  updateMailingAddress('street_address', value);
                }}
                placeholder="请输入街道地址（仅限英文）"
                required
              />
              <p className="text-xs text-amber-600 -mt-2">注意：仅支持英文字母、数字和常用符号（如 - . , # /）</p>

              <FormField
                label="街道地址（第二行）"
                value={(data.mailing_address && data.mailing_address.street_address_2) || ''}
                onChange={(e) => updateMailingAddress('street_address_2', e.target.value)}
                placeholder="公寓号、单元号等（可选）"
              />

              {/* 按照正确顺序：国家 → 州/省 → 城市 → 邮政编码 */}
              <div id="mailing_country" />
              <CountrySelect
                label="国家"
                value={(data.mailing_address && data.mailing_address.country) || ''}
                onChange={(value) => {
                  const currentMailingAddress = data.mailing_address || {};
                  onChange('mailing_address', {
                    ...currentMailingAddress,
                    country: value,
                    state: '', // 清空州/省选择
                    city: ''   // 清空城市选择
                  });
                }}
                required
              />

              {/* 根据国家选择显示不同的州/省和城市输入组件 */}
              {(() => {
                const country = (data.mailing_address && data.mailing_address.country) || '';
                const isChineseRegion = ['CHIN', 'MAC', 'HNK', 'TWAN'].includes(country);

                if (isChineseRegion) {
                  return (
                    <ProvinceCitySelect
                      provinceValue={(data.mailing_address && data.mailing_address.state) || ''}
                      cityValue={(data.mailing_address && data.mailing_address.city) || ''}
                      onProvinceChange={(value) => {
                        const currentMailingAddress = data.mailing_address || {};
                        onChange('mailing_address', {
                          ...currentMailingAddress,
                          state: value,
                          city: '' // 清空城市选择
                        });
                      }}
                      onCityChange={(value) => updateMailingAddress('city', value)}
                    />
                  );
                } else if (country === 'USA') {
                  // 美国使用专用的州选择器
                  return (
                    <div className="space-y-4">
                      <div id="mailing_state" />
                      <USStateSelect
                        label="州"
                        value={(data.mailing_address && data.mailing_address.state) || ''}
                        onChange={(value) => updateMailingAddress('state', value)}
                        required
                      />
                      <FormField
                        id="mailing_city"
                        label="城市"
                        value={(data.mailing_address && data.mailing_address.city) || ''}
                        onChange={(e) => updateMailingAddress('city', e.target.value)}
                        placeholder="Los Angeles"
                        required
                      />
                    </div>
                  );
                } else if (country) {
                  // 其他国家
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        id="mailing_state_text"
                        label="州/省"
                        value={(data.mailing_address && data.mailing_address.state) || ''}
                        onChange={(e) => updateMailingAddress('state', e.target.value)}
                        placeholder="省份或州名"
                        required
                      />
                      <FormField
                        id="mailing_city_text"
                        label="城市"
                        value={(data.mailing_address && data.mailing_address.city) || ''}
                        onChange={(e) => updateMailingAddress('city', e.target.value)}
                        placeholder="城市名"
                        required
                      />
                    </div>
                  );
                }
                return null;
              })()}

              {/* 邮政编码放在最后 */}
              {(() => {
                const country = (data.mailing_address && data.mailing_address.country) || '';
                const isChineseRegion = ['CHIN', 'MAC', 'HNK', 'TWAN'].includes(country);

                if (isChineseRegion) {
                  return (
                    <>
                      <div id="mailing_zip" />
                      <ChineseZIPCodeInput
                      label="邮政编码"
                      value={(data.mailing_address && data.mailing_address.zip_code) || ''}
                      onChange={(value) => updateMailingAddress('zip_code', value)}
                      required
                      countryCode={country}
                    />
                    {fieldErrors['#mailing_zip'] && (<div className="-mt-2 text-xs text-red-600">{fieldErrors['#mailing_zip']}</div>)}
                    </>
                  );
                } else if (country) {
                  return (
                    <>
                      <div id="mailing_zip" />
                      <ZIPCodeInput
                      label="邮政编码"
                      value={(data.mailing_address && data.mailing_address.zip_code) || ''}
                      onChange={(value) => updateMailingAddress('zip_code', value)}
                      required
                    />
                    {fieldErrors['#mailing_zip'] && (<div className="-mt-2 text-xs text-red-600">{fieldErrors['#mailing_zip']}</div>)}
                    </>
                  );
                }
                return null;
              })()}
            </div>
          )}
        </div>

        {/* 电话信息 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
            <Phone className="w-5 h-5 mr-2" />
            电话信息
          </h4>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <PhoneInput
                label="主要电话"
                value={data.primary_phone || ''}
                onChange={(value) => onChange('primary_phone', value)}
                placeholder="13800138000"
                required
              />
              {fieldErrors['#home_primary_phone'] && (
                <div className="-mt-2 text-xs text-red-600">{fieldErrors['#home_primary_phone']}</div>
              )}

              <PhoneInput
                label="次要电话"
                value={data.secondary_phone || ''}
                onChange={(value) => onChange('secondary_phone', value)}
                placeholder="13800138000"
              />

              <PhoneInput
                label="工作电话"
                value={data.work_phone || ''}
                onChange={(value) => onChange('work_phone', value)}
                placeholder="13800138000"
              />
            </div>

            {/* 其他电话 */}
            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-blue-400">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h5 className="text-lg font-semibold text-gray-800 flex items-center mb-1">
                    <Phone className="w-5 h-5 mr-2 text-blue-600" />
                    您是否在过去5年中使用过其他的电话号码？
                  </h5>
                  <p className="text-xs text-amber-600 ml-7">建议选择"否"，填写过多电话号码可能引起签证官不必要的关注。</p>
                </div>
                <div className="flex gap-4">
                  <div className="flex gap-6">
                    <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                      <input
                        type="radio"
                        name="other_phone_used"
                        value="false"
                        checked={data.other_phone_used === false}
                        onChange={(e) => {
                          onChange('other_phone_used', false);
                          onChange('other_phone', []);
                        }}
                        className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                      />
                      <span className="text-base font-medium text-gray-700">❌ 否 - 我没有其他电话号码</span>
                    </label>

                    <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                      <input
                        type="radio"
                        name="other_phone_used"
                        value="true"
                        checked={data.other_phone_used === true}
                        onChange={(e) => {
                          onChange('other_phone_used', true);
                          if (!data.other_phone || data.other_phone.length === 0) {
                            addOtherPhone();
                          }
                        }}
                        className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                      />
                      <span className="text-base font-medium text-gray-700">✅ 是 - 我有其他电话号码</span>
                    </label>
                  </div>

                  {data.other_phone_used && (
                    <button
                      type="button"
                      onClick={addOtherPhone}
                      className="flex items-center px-3 py-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      添加电话
                    </button>
                  )}
                </div>
              </div>

              {data.other_phone_used && (
                <div className="space-y-3">
                  {(() => {
                    const otherPhones = data.other_phone || [];
                    return otherPhones.map((phone, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="flex-1">
                          <PhoneInput
                            label={`其他电话 ${index + 1}`}
                            value={phone || ''}
                            onChange={(value) => updateOtherPhone(index, value)}
                            placeholder="13800138000"
                          />
                        </div>
                        {otherPhones.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeOtherPhone(index)}
                            className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md mt-6"
                            title="删除此电话"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 邮箱信息 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
            <Mail className="w-5 h-5 mr-2" />
            邮箱信息
          </h4>

          <div className="space-y-4">
            <div id="home_email" />
            <EmailInput
              label="主要邮箱"
              value={data.email || ''}
              onChange={(value) => onChange('email', value)}
              placeholder="TEST@TEST.com"
              required
            />
            {fieldErrors['#home_email'] && (
              <div className="-mt-2 text-xs text-red-600">{fieldErrors['#home_email']}</div>
            )}

            {/* 其他邮箱 */}
            <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-l-4 border-green-400">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h5 className="text-lg font-semibold text-gray-800 flex items-center mb-1">
                    <Mail className="w-5 h-5 mr-2 text-green-600" />
                    您是否在过去5年中使用过其他的电子邮件地址？
                  </h5>
                  <p className="text-xs text-amber-600 ml-7">建议选择"否"，填写过多邮箱地址可能引起签证官不必要的关注。</p>
                </div>
                <div className="flex gap-4">
                  <div className="flex gap-6">
                    <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-all duration-200">
                      <input
                        type="radio"
                        name="other_email_used"
                        value="false"
                        checked={data.other_email_used === false}
                        onChange={(e) => {
                          onChange('other_email_used', false);
                          onChange('other_email', []);
                        }}
                        className="mr-3 w-5 h-5 text-green-600 border-gray-300 focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                      />
                      <span className="text-base font-medium text-gray-700">❌ 否 - 我没有其他邮箱</span>
                    </label>

                    <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-all duration-200">
                      <input
                        type="radio"
                        name="other_email_used"
                        value="true"
                        checked={data.other_email_used === true}
                        onChange={(e) => {
                          onChange('other_email_used', true);
                          if (!data.other_email || data.other_email.length === 0) {
                            addOtherEmail();
                          }
                        }}
                        className="mr-3 w-5 h-5 text-green-600 border-gray-300 focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                      />
                      <span className="text-base font-medium text-gray-700">✅ 是 - 我有其他邮箱</span>
                    </label>
                  </div>

                  {data.other_email_used && (
                    <button
                      type="button"
                      onClick={addOtherEmail}
                      className="flex items-center px-3 py-1 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      添加邮箱
                    </button>
                  )}
                </div>
              </div>

              {data.other_email_used && (
                <div className="space-y-3">
                  {(() => {
                    const otherEmails = data.other_email || [];
                    return otherEmails.map((email, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="flex-1">
                          <EmailInput
                            label={`其他邮箱 ${index + 1}`}
                            value={email || ''}
                            onChange={(value) => updateOtherEmail(index, value)}
                            placeholder="114514@TEST.com"
                          />
                        </div>
                        {otherEmails.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeOtherEmail(index)}
                            className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md mt-6"
                            title="删除此邮箱"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 社交媒体信息 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-lg font-semibold text-gray-800 flex items-center">
                <Users className="w-5 h-5 mr-2" />
                您有一个社交媒体吗？
              </h4>
              <p className="text-xs text-amber-600 ml-7 mt-1">建议：仅填写一个（如QQ QZONE 或微博weibo），填写过多社交媒体可能引起不必要的关注。</p>
              <p className="text-xs text-amber-600 ml-7">提前检查自己的公开内容，避免：明显的仇恨、暴力、极端主义言论；非法活动"炫耀"；明确"永久滞留""非法移民"之类表述。</p>
            </div>
            <button
              type="button"
              onClick={addSocialMedia}
              className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
            >
              <Plus className="w-4 h-4 mr-1" />
              添加社交媒体
            </button>
          </div>

          <div className="space-y-4">
            {(() => {
              const socialMediaList = data.social_media || [];
              return socialMediaList.map((social, index) => (
                <div key={index} className="p-4 bg-gradient-to-r from-gray-50 to-purple-50 rounded-lg border-l-4 border-purple-400 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          社交媒体平台 {index + 1}
                        </label>
                        <select
                          value={social.social_media || ''}
                          onChange={(e) => updateSocialMedia(index, 'social_media', e.target.value)}
                          className="w-full h-12 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gradient-to-r from-white to-purple-50 shadow-sm hover:shadow-md transition-all duration-200"
                        >
                          <option value="">请选择平台...</option>
                          {SOCIAL_MEDIA_PLATFORMS.map(platform => (
                            <option key={platform.value} value={platform.value}>
                              {platform.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <FormField
                        label={`账号标识 ${index + 1}`}
                        value={social.social_media_identifier || ''}
                        onChange={(e) => updateSocialMedia(index, 'social_media_identifier', e.target.value)}
                        placeholder="123456789"
                      />
                    </div>
                    {socialMediaList.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSocialMedia(index)}
                        className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md mt-6"
                        title="删除此社交媒体"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>

        {/* 共享社交媒体信息 */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-2">
              <Globe className="w-5 h-5 mr-2" />
              您是否希望提供有关您在过去五年内用于创建或共享内容（照片、视频、状态更新等）的任何其他网站或应用程序上的状态的信息？
            </h4>
            <p className="text-xs text-amber-600 ml-7">建议：仅填写一个（如QQ QZONE 或微博weibo），填写过多社交媒体可能引起不必要的关注。</p>
            <p className="text-xs text-amber-600 ml-7 mb-4">提前检查自己的公开内容，避免：明显的仇恨、暴力、极端主义言论；非法活动"炫耀"；明确"永久滞留""非法移民"之类表述。</p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                从下面的列表中选择您在过去五年中使用过的每个社交媒体平台。在平台名称旁边的空格中，输入您在该平台上使用的用户名或昵称。请不要提供您的密码。如果在使用了多个社交平台或在一个平台上使用了多个用户名或昵称 ，请点击“添加另一个”按钮分别列出每个平台。如果您在过去五年内没有使用任何列出的社交媒体平台，请选择“无”。
              </label>
              <div className="flex gap-6">
                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="has_share_social_media"
                    value="false"
                    checked={!Array.isArray(data.share_social_media)}
                    onChange={(e) => {
                      onChange('share_social_media', false);
                    }}
                    className="mr-3 w-5 h-5 text-indigo-600 border-gray-300 focus:ring-indigo-500 focus:ring-2"
                  />
                  <span className="text-base font-medium text-gray-700">❌ 否 - 无需共享</span>
                </label>

                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="has_share_social_media"
                    value="true"
                    checked={Array.isArray(data.share_social_media)}
                    onChange={(e) => {
                      if (!Array.isArray(data.share_social_media)) {
                        addShareSocialMedia();
                      }
                    }}
                    className="mr-3 w-5 h-5 text-indigo-600 border-gray-300 focus:ring-indigo-500 focus:ring-2"
                  />
                  <span className="text-base font-medium text-gray-700">✅ 是 - 有需要共享的账号</span>
                </label>
              </div>
            </div>
          </div>

          {Array.isArray(data.share_social_media) && (
            <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border-l-4 border-indigo-400">
              <div className="flex items-center justify-between mb-4">
                <h5 className="text-md font-semibold text-gray-700">共享账号列表</h5>
                <button
                  type="button"
                  onClick={addShareSocialMedia}
                  className="flex items-center px-3 py-1 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-lg hover:from-indigo-700 hover:to-indigo-800 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  添加共享账号
                </button>
              </div>

              <div className="space-y-4">
                {(() => {
                  const shareList = data.share_social_media || [];
                  return shareList.map((social, index) => (
                    <div key={index} className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              共享平台 {index + 1}
                            </label>
                            <select
                              value={social.social_media || ''}
                              onChange={(e) => updateShareSocialMedia(index, 'social_media', e.target.value)}
                              className="w-full h-12 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-gradient-to-r from-white to-indigo-50 shadow-sm hover:shadow-md transition-all duration-200"
                            >
                              <option value="">请选择平台...</option>
                              {SOCIAL_MEDIA_PLATFORMS.map(platform => (
                                <option key={platform.value} value={platform.value}>
                                  {platform.label}
                                </option>
                              ))}
                            </select>
                          </div>

                          <FormField
                            label={`共享账号标识 ${index + 1}`}
                            value={social.social_media_identifier || ''}
                            onChange={(e) => updateShareSocialMedia(index, 'social_media_identifier', e.target.value)}
                            placeholder="123456789"
                          />
                        </div>
                        {shareList.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeShareSocialMedia(index)}
                            className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md mt-6"
                            title="删除此共享账号"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}
        </div>

        {/* 预览信息 */}
        {(data.home_address || data.primary_phone || data.email) && (
          <div className="mt-6 p-4 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border-l-4 border-cyan-400 shadow-sm">
            <h4 className="font-semibold text-cyan-800 mb-2">信息预览</h4>
            <div className="text-sm text-cyan-700 space-y-1">
              {data.home_address && data.home_address.city && (
                <p><strong>家庭地址:</strong> {data.home_address.city}, {getCountryName(data.home_address.country)}</p>
              )}

              <p><strong>邮寄地址:</strong> {data.mailing_address_same ? '与家庭地址相同' : '单独邮寄地址'}</p>

              {data.primary_phone && <p><strong>主要电话:</strong> {data.primary_phone}</p>}

              {data.email && <p><strong>主要邮箱:</strong> {data.email}</p>}

              {data.other_phone_used && data.other_phone && data.other_phone.length > 0 && (
                <p><strong>其他电话数量:</strong> {data.other_phone.length} 个</p>
              )}

              {data.other_email_used && data.other_email && data.other_email.length > 0 && (
                <p><strong>其他邮箱数量:</strong> {data.other_email.length} 个</p>
              )}

              {data.social_media && data.social_media.length > 0 && (
                <p><strong>社交媒体账号:</strong> {data.social_media.length} 个</p>
              )}

              {Array.isArray(data.share_social_media) && data.share_social_media.length > 0 && (
                <p><strong>共享社交媒体:</strong> {data.share_social_media.length} 个</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressPhoneForm;
